import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useCallback, useState } from 'react'
import { useSaveToDirectory } from '../../hooks/useSaveToDirectory'
import SaveToDirectoryDialog from '../contact/SaveToDirectoryDialog'

export interface DesignatedDriver {
  id: string
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
  note: string
  /** Whether this driver was loaded from the backend (existing) vs newly created */
  isExisting?: boolean
}

interface DesignatedDriverFormProps {
  drivers: DesignatedDriver[]
  onChange: (drivers: DesignatedDriver[]) => void
  isDisabled?: boolean
  maxDrivers?: number
  minDrivers?: number
  /** Called when a driver's contact info should be checked for directory save */
  onDriverSaved?: (driver: DesignatedDriver) => void
}

const generateId = () =>
  `driver-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

const createEmptyDriver = (): DesignatedDriver => ({
  id: generateId(),
  firstName: '',
  lastName: '',
  phoneNumber: '',
  email: '',
  note: '',
  isExisting: false,
})

const DesignatedDriverForm: React.FC<DesignatedDriverFormProps> = ({
  drivers,
  onChange,
  isDisabled = false,
  maxDrivers = 20,
  minDrivers = 1,
  onDriverSaved,
}) => {
  const [errors, setErrors] = useState<Record<string, Record<string, string>>>(
    {},
  )

  // Hook for managing save-to-directory flow
  const {
    showDialog,
    pendingContact,
    checkAndPrompt,
    handleConfirm,
    handleCancel,
  } = useSaveToDirectory()

  const handleAddDriver = () => {
    if (drivers.length < maxDrivers) {
      onChange([...drivers, createEmptyDriver()])
    }
  }

  const handleRemoveDriver = (id: string) => {
    if (drivers.length > minDrivers) {
      onChange(drivers.filter((driver) => driver.id !== id))
      // Clear errors for removed driver
      const newErrors = { ...errors }
      delete newErrors[id]
      setErrors(newErrors)
    }
  }

  const handleFieldChange = (
    id: string,
    field: keyof DesignatedDriver,
    value: string,
  ) => {
    const driver = drivers.find((d) => d.id === id)

    // For existing drivers, name fields are read-only
    if (driver?.isExisting && (field === 'firstName' || field === 'lastName')) {
      return
    }

    onChange(
      drivers.map((driver) =>
        driver.id === id ? { ...driver, [field]: value } : driver,
      ),
    )

    // Clear error for this field when user starts typing
    if (errors[id]?.[field]) {
      const newErrors = { ...errors }
      delete newErrors[id][field]
      if (Object.keys(newErrors[id]).length === 0) {
        delete newErrors[id]
      }
      setErrors(newErrors)
    }
  }

  // Handle removing email/phone for existing drivers (remove-only, not editable)
  const handleRemoveContactField = (
    id: string,
    field: 'email' | 'phoneNumber',
  ) => {
    onChange(
      drivers.map((driver) =>
        driver.id === id ? { ...driver, [field]: '' } : driver,
      ),
    )
  }

  const validateField = (
    id: string,
    field: keyof DesignatedDriver,
    value: string,
  ) => {
    const newErrors = { ...errors }

    if (!newErrors[id]) {
      newErrors[id] = {}
    }

    // Required fields validation (except note)
    if (field !== 'note' && !value.trim()) {
      newErrors[id][field] = 'This field is required'
    } else if (field === 'email' && value.trim()) {
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(value)) {
        newErrors[id][field] = 'Please enter a valid email address'
      } else {
        delete newErrors[id][field]
      }
    } else if (field === 'phoneNumber' && value.trim()) {
      // Phone validation - allow various formats
      const phoneRegex = /^[\d\s\-\(\)\+]{10,}$/
      if (!phoneRegex.test(value.replace(/\s/g, ''))) {
        newErrors[id][field] = 'Please enter a valid phone number'
      } else {
        delete newErrors[id][field]
      }
    } else {
      delete newErrors[id][field]
    }

    if (Object.keys(newErrors[id]).length === 0) {
      delete newErrors[id]
    }

    setErrors(newErrors)
  }

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '')
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) {
      return digits
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
    }
  }

  const handlePhoneChange = (id: string, value: string) => {
    const driver = drivers.find((d) => d.id === id)
    // For existing drivers, phone is remove-only
    if (driver?.isExisting) {
      return
    }
    const formatted = formatPhoneNumber(value)
    handleFieldChange(id, 'phoneNumber', formatted)
  }

  const handleEmailChange = (id: string, value: string) => {
    const driver = drivers.find((d) => d.id === id)
    // For existing drivers, email is remove-only
    if (driver?.isExisting) {
      return
    }
    handleFieldChange(id, 'email', value)
  }

  // Check if driver should be prompted to save to directory after email/phone entered
  const handleEmailBlur = useCallback(
    async (driver: DesignatedDriver) => {
      if (driver.email && !driver.isExisting) {
        const name = `${driver.firstName} ${driver.lastName}`.trim()
        await checkAndPrompt({
          email: driver.email,
          name: name || undefined,
          phoneNumber: driver.phoneNumber || undefined,
        })
      }
    },
    [checkAndPrompt],
  )

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.header)}>
        <h4 className={css(styles.title)}>Designated Drivers</h4>
        <span className={css(styles.count)}>
          {drivers.length} / {maxDrivers} drivers
        </span>
      </div>

      <div className={css(styles.driversList)}>
        {drivers.map((driver, index) => (
          <div key={driver.id} className={css(styles.driverCard)}>
            <div className={css(styles.driverHeader)}>
              <span className={css(styles.driverNumber)}>
                Driver {index + 1}
                {driver.isExisting && (
                  <span className={css(styles.existingBadge)}>Saved</span>
                )}
              </span>
              {drivers.length > minDrivers && (
                <button
                  type="button"
                  onClick={() => handleRemoveDriver(driver.id)}
                  className={css(styles.removeButton)}
                  disabled={isDisabled}
                  aria-label={`Remove driver ${index + 1}`}
                >
                  <i className="fa-solid fa-times" />
                </button>
              )}
            </div>

            <div className={css(styles.fieldsGrid)}>
              {/* First Name - Read-only for existing drivers */}
              <div className={css(styles.fieldGroup)}>
                <label className={css(styles.label)}>
                  First Name <span className={css(styles.required)}>*</span>
                  {driver.isExisting && (
                    <i
                      className="fa-solid fa-lock"
                      style={{ marginLeft: 4, fontSize: 10, color: '#9CA3AF' }}
                    />
                  )}
                </label>
                {driver.isExisting ? (
                  <div className={css(styles.readOnlyValue)}>
                    {driver.firstName || '-'}
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      value={driver.firstName}
                      onChange={(e) =>
                        handleFieldChange(
                          driver.id,
                          'firstName',
                          e.target.value,
                        )
                      }
                      onBlur={(e) =>
                        validateField(driver.id, 'firstName', e.target.value)
                      }
                      className={css(
                        styles.input,
                        errors[driver.id]?.firstName && styles.inputError,
                      )}
                      placeholder="First name"
                      disabled={isDisabled}
                    />
                    {errors[driver.id]?.firstName && (
                      <span className={css(styles.errorText)}>
                        {errors[driver.id].firstName}
                      </span>
                    )}
                  </>
                )}
              </div>

              {/* Last Name - Read-only for existing drivers */}
              <div className={css(styles.fieldGroup)}>
                <label className={css(styles.label)}>
                  Last Name <span className={css(styles.required)}>*</span>
                  {driver.isExisting && (
                    <i
                      className="fa-solid fa-lock"
                      style={{ marginLeft: 4, fontSize: 10, color: '#9CA3AF' }}
                    />
                  )}
                </label>
                {driver.isExisting ? (
                  <div className={css(styles.readOnlyValue)}>
                    {driver.lastName || '-'}
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      value={driver.lastName}
                      onChange={(e) =>
                        handleFieldChange(driver.id, 'lastName', e.target.value)
                      }
                      onBlur={(e) =>
                        validateField(driver.id, 'lastName', e.target.value)
                      }
                      className={css(
                        styles.input,
                        errors[driver.id]?.lastName && styles.inputError,
                      )}
                      placeholder="Last name"
                      disabled={isDisabled}
                    />
                    {errors[driver.id]?.lastName && (
                      <span className={css(styles.errorText)}>
                        {errors[driver.id].lastName}
                      </span>
                    )}
                  </>
                )}
              </div>

              {/* Phone Number - Remove-only for existing drivers */}
              <div className={css(styles.fieldGroup)}>
                <label className={css(styles.label)}>
                  Phone Number <span className={css(styles.required)}>*</span>
                </label>
                {driver.isExisting && driver.phoneNumber ? (
                  <div className={css(styles.removableField)}>
                    <span className={css(styles.removableValue)}>
                      {driver.phoneNumber}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        handleRemoveContactField(driver.id, 'phoneNumber')
                      }
                      className={css(styles.removeFieldButton)}
                      disabled={isDisabled}
                      title="Remove phone number"
                    >
                      <i className="fa-solid fa-times" />
                    </button>
                  </div>
                ) : driver.isExisting && !driver.phoneNumber ? (
                  <div className={css(styles.emptyValue)}>Not set</div>
                ) : (
                  <>
                    <input
                      type="tel"
                      value={driver.phoneNumber}
                      onChange={(e) =>
                        handlePhoneChange(driver.id, e.target.value)
                      }
                      onBlur={(e) =>
                        validateField(driver.id, 'phoneNumber', e.target.value)
                      }
                      className={css(
                        styles.input,
                        errors[driver.id]?.phoneNumber && styles.inputError,
                      )}
                      placeholder="(555) 555-5555"
                      disabled={isDisabled}
                    />
                    {errors[driver.id]?.phoneNumber && (
                      <span className={css(styles.errorText)}>
                        {errors[driver.id].phoneNumber}
                      </span>
                    )}
                  </>
                )}
              </div>

              {/* Email Address - Remove-only for existing drivers */}
              <div className={css(styles.fieldGroup)}>
                <label className={css(styles.label)}>
                  Email Address <span className={css(styles.required)}>*</span>
                </label>
                {driver.isExisting && driver.email ? (
                  <div className={css(styles.removableField)}>
                    <span className={css(styles.removableValue)}>
                      {driver.email}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        handleRemoveContactField(driver.id, 'email')
                      }
                      className={css(styles.removeFieldButton)}
                      disabled={isDisabled}
                      title="Remove email"
                    >
                      <i className="fa-solid fa-times" />
                    </button>
                  </div>
                ) : driver.isExisting && !driver.email ? (
                  <div className={css(styles.emptyValue)}>Not set</div>
                ) : (
                  <>
                    <input
                      type="email"
                      value={driver.email}
                      onChange={(e) =>
                        handleEmailChange(driver.id, e.target.value)
                      }
                      onBlur={(e) => {
                        validateField(driver.id, 'email', e.target.value)
                        handleEmailBlur(driver)
                      }}
                      className={css(
                        styles.input,
                        errors[driver.id]?.email && styles.inputError,
                      )}
                      placeholder="driver@example.com"
                      disabled={isDisabled}
                    />
                    {errors[driver.id]?.email && (
                      <span className={css(styles.errorText)}>
                        {errors[driver.id].email}
                      </span>
                    )}
                  </>
                )}
              </div>

              <div className={css(styles.fieldGroup, styles.fullWidth)}>
                <label className={css(styles.label)}>
                  Note <span className={css(styles.optional)}>(optional)</span>
                </label>
                <textarea
                  value={driver.note}
                  onChange={(e) =>
                    handleFieldChange(driver.id, 'note', e.target.value)
                  }
                  className={css(styles.textarea)}
                  placeholder="Additional notes about this driver..."
                  rows={2}
                  disabled={isDisabled}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {drivers.length < maxDrivers && (
        <button
          type="button"
          onClick={handleAddDriver}
          className={css(styles.addButton)}
          disabled={isDisabled}
        >
          <i className="fa-solid fa-plus" style={{ marginRight: '8px' }} />
          Add Driver
        </button>
      )}

      {/* Save to Directory Dialog */}
      {showDialog && pendingContact && (
        <SaveToDirectoryDialog
          email={pendingContact.email}
          name={pendingContact.name}
          phoneNumber={pendingContact.phoneNumber}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    marginBottom: '24px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  title: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: '#111827',
  },
  count: {
    fontSize: '12px',
    color: '#6B7280',
  },
  driversList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  driverCard: {
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    padding: '16px',
    border: '1px solid #E5E7EB',
  },
  driverHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  driverNumber: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  existingBadge: {
    fontSize: '10px',
    fontWeight: 500,
    color: '#059669',
    backgroundColor: '#D1FAE5',
    padding: '2px 6px',
    borderRadius: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  removeButton: {
    background: 'none',
    border: 'none',
    color: '#EF4444',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '14px',
    ':hover': {
      backgroundColor: '#FEE2E2',
    },
    ':disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  fieldsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  label: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#374151',
    display: 'flex',
    alignItems: 'center',
  },
  required: {
    color: '#EF4444',
  },
  optional: {
    fontWeight: 400,
    color: '#6B7280',
  },
  input: {
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '4px',
    fontSize: '14px',
    color: '#111827',
    backgroundColor: 'white',
    ':focus': {
      outline: 'none',
      borderColor: '#2563EB',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    },
    ':disabled': {
      backgroundColor: '#F3F4F6',
      cursor: 'not-allowed',
    },
  },
  inputError: {
    borderColor: '#EF4444',
    ':focus': {
      borderColor: '#EF4444',
      boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.1)',
    },
  },
  textarea: {
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '4px',
    fontSize: '14px',
    color: '#111827',
    backgroundColor: 'white',
    resize: 'vertical',
    fontFamily: 'inherit',
    ':focus': {
      outline: 'none',
      borderColor: '#2563EB',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    },
    ':disabled': {
      backgroundColor: '#F3F4F6',
      cursor: 'not-allowed',
    },
  },
  errorText: {
    fontSize: '11px',
    color: '#EF4444',
  },
  addButton: {
    marginTop: '16px',
    padding: '10px 16px',
    backgroundColor: '#EFF6FF',
    color: '#2563EB',
    border: '1px dashed #2563EB',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      backgroundColor: '#DBEAFE',
    },
    ':disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  // New styles for read-only and removable fields
  readOnlyValue: {
    padding: '8px 12px',
    backgroundColor: '#F3F4F6',
    border: '1px solid #E5E7EB',
    borderRadius: '4px',
    fontSize: '14px',
    color: '#374151',
  },
  removableField: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '4px',
  },
  removableValue: {
    fontSize: '14px',
    color: '#111827',
  },
  removeFieldButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#6B7280',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    ':hover': {
      backgroundColor: '#FEE2E2',
      color: '#DC2626',
    },
    ':disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  emptyValue: {
    padding: '8px 12px',
    backgroundColor: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '4px',
    fontSize: '14px',
    color: '#9CA3AF',
    fontStyle: 'italic',
  },
})

export default DesignatedDriverForm
